import z from 'zod'

/** Management app: PUT body to set server-desired overlay visibility (`null` clears mandate for that axis). */
export const playerOverlayUiVisibilityBodySchema = z.object({
  timersVisible: z.boolean().nullable().optional(),
  statusVisible: z.boolean().nullable().optional()
})

export type TPlayerOverlayUiVisibilityBody = z.infer<typeof playerOverlayUiVisibilityBodySchema>

/** WebSocket: tenant DO → player, idempotent set timers overlay. */
export const setTimersOverlayWsPayloadSchema = z.object({
  visible: z.boolean()
})

export type TSetTimersOverlayWsPayload = z.infer<typeof setTimersOverlayWsPayloadSchema>

/** WebSocket: tenant DO → player, idempotent set current-status (debug) overlay. */
export const setCurrentStatusOverlayWsPayloadSchema = z.object({
  visible: z.boolean()
})

export type TSetCurrentStatusOverlayWsPayload = z.infer<typeof setCurrentStatusOverlayWsPayloadSchema>

/** Registration-status: only keys with an active server mandate are present. */
export const remoteOverlayUiSchema = z.object({
  timersVisible: z.boolean().optional(),
  statusVisible: z.boolean().optional()
})

export type TRemoteOverlayUi = z.infer<typeof remoteOverlayUiSchema>

/** Safe integer seq for overlay directives on the client (registration + heartbeat). */
export const overlayUiDirectiveSeqFieldSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER)

/**
 * `remoteOverlayUi` in API responses: known keys only, at least one visibility field (S2).
 * Stricter than {@link remoteOverlayUiSchema} alone (`.strict()` rejects unknown nested keys).
 */
export const strictRemoteOverlayUiDirectiveSchema = remoteOverlayUiSchema.strict().refine(
  data => data.timersVisible !== undefined || data.statusVisible !== undefined,
  { message: 'remoteOverlayUi must include at least one visibility key' }
)

export interface TRegistrationStatusOverlayFields {
  remoteOverlayUi: TRemoteOverlayUi
  overlayUiDirectiveSeq?: number
}

/**
 * Validates registration-status `remoteOverlayUi` / optional `overlayUiDirectiveSeq` before the player applies them.
 * Returns `null` if `remoteOverlayUi` is missing, invalid, or contains unknown keys.
 */
export function parseRegistrationStatusOverlayFields(input: {
  remoteOverlayUi?: unknown
  overlayUiDirectiveSeq?: unknown
}): TRegistrationStatusOverlayFields | null {
  if (input.remoteOverlayUi == null || typeof input.remoteOverlayUi !== 'object') {
    return null
  }
  const ui = strictRemoteOverlayUiDirectiveSchema.safeParse(input.remoteOverlayUi)
  if (!ui.success) {
    return null
  }
  const out: TRegistrationStatusOverlayFields = { remoteOverlayUi: ui.data }
  if (input.overlayUiDirectiveSeq !== undefined) {
    const seq = overlayUiDirectiveSeqFieldSchema.safeParse(input.overlayUiDirectiveSeq)
    if (seq.success) {
      out.overlayUiDirectiveSeq = seq.data
    }
  }
  return out
}

/** Build `remoteOverlayUi` from DB desired columns (null = no mandate for that axis). */
export function buildRemoteOverlayUiFromDesiredColumns(player: {
  timersOverlayDesired: boolean | null
  currentStatusOverlayDesired: boolean | null
}): TRemoteOverlayUi | undefined {
  const remoteOverlayUi: TRemoteOverlayUi = {}
  if (player.timersOverlayDesired != null) {
    remoteOverlayUi.timersVisible = player.timersOverlayDesired
  }
  if (player.currentStatusOverlayDesired != null) {
    remoteOverlayUi.statusVisible = player.currentStatusOverlayDesired
  }
  return Object.keys(remoteOverlayUi).length > 0 ? remoteOverlayUi : undefined
}

/**
 * POST /playback-heartbeat JSON body when the server has directives for the player (else 204).
 * Overlay pair must appear together; `assetCacheRepairDirectiveSeq` may appear alone or with overlay.
 */
export const playbackHeartbeatDirectiveResponseSchema = z
  .object({
    remoteOverlayUi: strictRemoteOverlayUiDirectiveSchema.optional(),
    overlayUiDirectiveSeq: overlayUiDirectiveSeqFieldSchema.optional(),
    assetCacheRepairDirectiveSeq: overlayUiDirectiveSeqFieldSchema.optional()
  })
  .strict()
  .refine(
    d => (d.remoteOverlayUi !== undefined) === (d.overlayUiDirectiveSeq !== undefined),
    { message: 'remoteOverlayUi and overlayUiDirectiveSeq must appear together' }
  )
  .refine(
    d => d.remoteOverlayUi !== undefined || d.assetCacheRepairDirectiveSeq !== undefined,
    { message: 'must include overlay directive pair and/or assetCacheRepairDirectiveSeq' }
  )
  .refine(
    d => d.assetCacheRepairDirectiveSeq === undefined || d.assetCacheRepairDirectiveSeq >= 1,
    { message: 'assetCacheRepairDirectiveSeq must be at least 1 when present' }
  )

export type TPlaybackHeartbeatDirectiveResponse = z.infer<typeof playbackHeartbeatDirectiveResponseSchema>

/**
 * Legacy overlay-only shape (subset of {@link playbackHeartbeatDirectiveResponseSchema}).
 * @deprecated Prefer `playbackHeartbeatDirectiveResponseSchema` for new code.
 */
export const playbackHeartbeatOverlayResponseSchema = z.object({
  remoteOverlayUi: strictRemoteOverlayUiDirectiveSchema,
  overlayUiDirectiveSeq: overlayUiDirectiveSeqFieldSchema
}).strict()

export type TPlaybackHeartbeatOverlayResponse = z.infer<typeof playbackHeartbeatOverlayResponseSchema>
